import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import { RHFAutoCompleteVirtualize } from '@openx/components-core/lib/AutoComplete/RHFAutoCompleteVirtualize.tsx';
import { RHFCheckboxField } from '@openx/components-core/lib/CheckboxField/RHFCheckboxField.tsx';
import { RHFDateTimePickerField } from '@openx/components-core/lib/DateTimePickerField/RHFDateTimePickerField.tsx';
import { FormSection } from '@openx/components-core/lib/FormSection/FormSection.tsx';
import { IndentedSections } from '@openx/components-core/lib/IndentedSection/IndentedSections.tsx';
import { RHFRadioField } from '@openx/components-core/lib/RadioField/RHFRadioField.tsx';
import { RHFTextFieldWithAdornment } from '@openx/components-core/lib/TextField/RHFTextFieldWithAdornment.tsx';
import { useTargetingContext } from '@openx/components-targeting/utils/context/TargetingContext.tsx';
import { RIGHT_COLUMN_WIDTH } from '@openx/constants/form.ts';
import { type DealFormData, dealFormField, dealStatus, pmpDealType } from '@openx/types/deal.ts';
import { useIsMounted } from '@openx/utils/hooks/useIsMounted.ts';
import { currencySymbol } from '@openx/utils/lib/priceFormat.ts';
import {
  DEAL_SOURCE_DSP_SOURCED,
  DV360_DEMAND_PARTNER_ID,
  TTD_DEMAND_PARTNER_ID,
} from '@openx/utils/services/config.ts';
import { type JSX, useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DealParticipantsField } from '../../components/DealParticipants/fields.ts';
import { useDealContext } from '../../utils/context/DealContext.tsx';
import { useDealFormContext } from '../DealForm.context.tsx';
import { useFixedCpmEnabledOptions } from '../hooks/useFixedCpmEnabledOptions.ts';
import { getDealsLabels } from '../utils/dealFormUtils.ts';
import { useFieldsVisibility } from './utils/useFieldsVisibility.ts';

const StyledIndentedSectionContainer = styled(Box)`
  margin-top: ${({ theme }) => theme.spacing(1)};
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};
`;

type DealFloorAndDurationFormSectionProps = {
  isEdit: boolean;
  index?: number;
  renderDates?: boolean;
};

export const DealFloorAndDurationFormSection = ({
  isEdit,
  index = 0,
  renderDates = true,
}: DealFloorAndDurationFormSectionProps): JSX.Element => {
  const { t } = useTranslation();
  const { useCurrenciesFormFetch, isOa } = useDealContext();
  const { useCurrentInstanceState } = useTargetingContext();
  const { isBefInstance } = useCurrentInstanceState();
  const { currencies } = useCurrenciesFormFetch();
  const { stateFieldsVisibility, dispatchFieldsVisibilityChange } = useFieldsVisibility();
  const { fixedCpmEnabledOptions } = useFixedCpmEnabledOptions();
  const isMounted = useIsMounted();
  const {
    getValues,
    formState: { isSubmitting },
    setValue,
    trigger,
  } = useFormContext<DealFormData>();
  const { dealInitialValues } = useDealFormContext();

  const observableCurrency = useWatch<Pick<DealFormData, 'currency'>>({
    name: dealFormField.CURRENCY,
  });
  const observableDealType = useWatch<Pick<DealFormData, 'pmp_deal_type'>>({
    name: dealFormField.PMP_DEAL_TYPE,
  });
  const observableDemandPartner = useWatch<DealFormData, 'deal_participants'>({
    name: dealFormField.DEAL_PARTICIPANTS,
  });
  const observableDealSource = useWatch<Pick<DealFormData, 'deal_source'>>({
    name: dealFormField.DEAL_SOURCE,
  });
  const observableDealPrice = useWatch<Pick<DealFormData, 'deal_price'>>({
    name: dealFormField.DEAL_PRICE,
  });

  const canRemovePriceFloor = observableDealPrice !== '' && isEdit;

  const demandPartnerId = observableDemandPartner?.[index][DealParticipantsField.DSP];
  const isTTDPartnerAndGuaranteedDealType =
    demandPartnerId && demandPartnerId === TTD_DEMAND_PARTNER_ID && observableDealType === pmpDealType.Guaranteed;
  const hasDV360DemandPartner =
    demandPartnerId === DV360_DEMAND_PARTNER_ID ||
    observableDemandPartner?.some(participant => participant.demand_partner === DV360_DEMAND_PARTNER_ID);

  const dealsLabels = useMemo(() => getDealsLabels(t), [t]);
  const currencyOptions = useMemo(() => Object.keys(currencies.mapById), [currencies]);

  const isPublisher = !isOa && !isBefInstance;
  const shouldShowMaxFloor = stateFieldsVisibility.showMaxFloor && isPublisher;
  const initialDealType = dealInitialValues?.[dealFormField.PMP_DEAL_TYPE];

  const handleChangeMaxFloor = useCallback(() => {
    if (initialDealType === observableDealType) {
      return;
    }

    const isNotUsingMarketFloor = !isEdit || !getValues(dealFormField.USE_MARKET_FLOOR);

    if (shouldShowMaxFloor && isNotUsingMarketFloor && isPublisher) {
      setValue(dealFormField.USE_MARKET_FLOOR, true, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [initialDealType, observableDealType, shouldShowMaxFloor, isEdit, getValues, isPublisher, setValue]);

  const endDateFieldLabel = useMemo(
    () =>
      `${dealsLabels[dealFormField.END_DATE]} ${
        !(
          isTTDPartnerAndGuaranteedDealType ||
            (hasDV360DemandPartner && observableDealSource !== DEAL_SOURCE_DSP_SOURCED)
        )
          ? t('(Optional)')
          : ''
      }`,
    [dealsLabels, hasDV360DemandPartner, isTTDPartnerAndGuaranteedDealType, observableDealSource, t]
  );

  const handleRemovePriceFloor = useCallback(() => {
    setValue(dealFormField.DEAL_PRICE, '', {
      shouldDirty: true,
      shouldValidate: false,
    });
  }, [setValue]);

  const handleDateChange = useCallback(
    (value: Date | null, dateType: typeof dealFormField.END_DATE | typeof dealFormField.START_DATE) => {
      setValue(dateType, value, {
        shouldDirty: true,
        shouldValidate: true,
      });

      if (dateType === dealFormField.START_DATE) {
        trigger(dealFormField.END_DATE);
      }

      if (dateType === dealFormField.END_DATE) {
        const endDateInFuture = value && new Date(value) > new Date();

        trigger(dealFormField.START_DATE);

        if (endDateInFuture) {
          setValue(dealFormField.STATUS, dealStatus.Active, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }
      }
    },
    [trigger, setValue]
  );

  useEffect(() => {
    dispatchFieldsVisibilityChange(observableDealType);
  }, [observableDealType, dispatchFieldsVisibilityChange]);

  useEffect(() => {
    if (isMounted()) {
      trigger(dealFormField.END_DATE);
    }
  }, [trigger, isMounted]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: all necessary deps are included intentionally
  useEffect(() => {
    handleChangeMaxFloor();
  }, [handleChangeMaxFloor, observableDealType]);

  return (
    <FormSection
      divider
      title={renderDates ? t('Deal Floor & Duration') : t('Deal Floor')}
      rightColumnSize={RIGHT_COLUMN_WIDTH}
    >
      {isBefInstance && (
        <RHFRadioField
          aria-label={t('Deal floor type')}
          name={dealFormField.FIXED_CPM_ENABLED}
          options={fixedCpmEnabledOptions}
          row
          sx={{ mb: 1 }}
        />
      )}

      <RHFAutoCompleteVirtualize
        name={dealFormField.CURRENCY}
        textFieldProps={{
          label: dealsLabels[dealFormField.CURRENCY],
          margin: 'dense',
          placeholder: t('[ Select Currency ]'),
        }}
        disableClearable
        renderOptions={{
          dataTest: 'deal-currency-item',
        }}
        getOptionLabel={option => currencies.mapById[option]?.name || option}
        options={currencyOptions}
        disabled={isEdit || isSubmitting}
        data-test="deal-currency"
      />

      <RHFTextFieldWithAdornment
        dataTest="deal-price"
        formControlProps={{ margin: 'dense' }}
        label={`${dealsLabels[dealFormField.DEAL_PRICE]} ${t('(Optional)')}`}
        name={dealFormField.DEAL_PRICE}
        placeholder="0.01"
        startAdornment={observableCurrency && currencySymbol(observableCurrency)}
        endAdornment={
          canRemovePriceFloor && (
            <IconButton onClick={handleRemovePriceFloor} size="small">
              <Close />
            </IconButton>
          )
        }
        slotProps={{ input: { sx: { marginTop: 0 } } }}
      />

      {shouldShowMaxFloor && (
        <StyledIndentedSectionContainer>
          <IndentedSections>
            <RHFCheckboxField
              name={dealFormField.USE_MARKET_FLOOR}
              label={dealsLabels[dealFormField.USE_MARKET_FLOOR]}
              data-test="use-max-of-deal-price-or-exchange-floors-as-floor"
            />
          </IndentedSections>
        </StyledIndentedSectionContainer>
      )}

      {renderDates && (
        <Grid container spacing={1}>
          <Grid size={6}>
            <RHFDateTimePickerField
              name={dealFormField.START_DATE}
              label={dealsLabels[dealFormField.START_DATE]}
              margin="dense"
              data-test="deal-start-date"
              onChange={value => handleDateChange(value, dealFormField.START_DATE)}
              showTodayButton
            />
          </Grid>

          <Grid size={6}>
            <RHFDateTimePickerField
              name={dealFormField.END_DATE}
              label={endDateFieldLabel}
              disablePast={getValues(dealFormField.STATUS) !== dealStatus.Expired}
              clearable
              margin="dense"
              data-test="deal-end-date"
              onChange={value => handleDateChange(value, dealFormField.END_DATE)}
              showTodayButton
            />
          </Grid>
        </Grid>
      )}
    </FormSection>
  );
};
